import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, InputField, RadioGroup, Select, SelectOption } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope } from '../../../graphql/__generated__/schema';
import { AccountActivitiesDocument } from '../../../graphql/__generated__/types';
import ActivityPicker from './ActivityPicker';
import { DraftAttachment, initialActivityIds } from './draft';
import { OnboardingRole, findRole, roleRefusal } from './grantableRoles';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
    attachment: DraftAttachment;
    accountName: string;
    /** The address entered at step 1. It is what designates the person: the journey resolves no contact. */
    email: string;
    roles: OnboardingRole[];
    onChange: (patch: Partial<DraftAttachment>) => void;
    onRemove: () => void;
    onEditIdentity: () => void;
}

/** One account: a role and a perimeter. Markets are only fetched when the perimeter is restricted. */
const AttachmentRow = ({ attachment, accountName, email, roles, onChange, onRemove, onEditIdentity }: Props) => {
    const restricted = attachment.dataScope === DataScope.Restricted;

    const [search, setSearch] = useState('');
    // Debounced: the value that goes to the API lags behind what is typed.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // The account travels as an argument, never as x-organization-id: this screen configures
    // accounts other than the actor's, and the header would fail each call on a refusal.
    const {
        data: activityData,
        previousData: previousActivityData,
        loading: activitiesLoading,
        error: activitiesError,
        refetch: refetchActivities,
    } = useQuery(AccountActivitiesDocument, {
        variables: { organizationId: attachment.organizationId, email, search: debouncedSearch },
        skip: !restricted,
    });
    // Keep the previous list on screen while the next arrives, but only once the pre-ticking
    // has been applied to THIS address, else it would show the former person's activities.
    const selectedCount = attachment.needIds.length + attachment.serviceIds.length;
    const suggestionApplied = attachment.suggestion?.email === email;
    const activities =
        activityData?.accountActivities ??
        (suggestionApplied ? (previousActivityData?.accountActivities ?? null) : null);

    // Applied once per (account × address): the loading guard prevents ticking the former
    // person's markets under a new address. A failed query writes no snapshot, so retry still initialises.
    useEffect(() => {
        if (!restricted || activitiesLoading || !activities) {
            return;
        }
        if (attachment.suggestion?.email === email) {
            return;
        }
        // Pre-tick only on the full list: under a search it would tick only what the search
        // shows, yet the snapshot would record it as applied.
        if (search.trim().length > 0 || debouncedSearch.trim().length > 0) {
            return;
        }
        const suggestedNeeds = activities.needs.filter((n) => n.linkedToContact).map((n) => n.id as string);
        const suggestedServices = activities.marketGroups
            .flatMap((group) => group.services)
            .filter((s) => s.linkedToContact)
            .map((s) => s.id as string);
        onChange({
            ...initialActivityIds(attachment, suggestedNeeds, suggestedServices),
            suggestion: {
                email,
                // Store what the pre-ticking ADDED, never what the source designates: see draft.ts.
                needIds: suggestedNeeds,
                serviceIds: suggestedServices,
                matchedContactCount: activities.matchedContactCount,
            },
        });
    }, [restricted, activitiesLoading, activities, email, search, debouncedSearch, attachment, onChange]);

    // Refused roles are disabled rather than removed, so the list never reads as incomplete.
    const roleOptions: SelectOption<string>[] = roles
        .filter((role) => role.id)
        .map((role) => ({
            label: role.name ?? '',
            value: role.id as string,
            disabled: !role.grantableByViewer,
            disabledReason: role.grantableByViewer ? undefined : roleRefusal(role.notGrantableReason).tag,
        }));

    // A role picked here that has stopped being grantable stays selected and named.
    const chosen = findRole(roles, attachment.roleId);
    const chosenRefusal = chosen?.grantableByViewer === false ? roleRefusal(chosen.notGrantableReason) : null;

    const toggleNeed = (needId: string, checked: boolean) =>
        onChange({
            needIds: checked ? [...attachment.needIds, needId] : attachment.needIds.filter((id) => id !== needId),
        });

    const toggleService = (serviceId: string, checked: boolean) =>
        onChange({
            serviceIds: checked
                ? [...attachment.serviceIds, serviceId]
                : attachment.serviceIds.filter((id) => id !== serviceId),
        });

    // Checking a market checks its services (never the market itself), and only those on screen.
    const toggleMarket = (ids: string[], checked: boolean) =>
        onChange({
            serviceIds: checked
                ? [...new Set([...attachment.serviceIds, ...ids])]
                : attachment.serviceIds.filter((id) => !ids.includes(id)),
        });

    // Read from the applied suggestion, not the query, so badges and ticks can't disagree.
    const applied = attachment.suggestion?.email === email ? attachment.suggestion : null;
    const suggestedCount = (applied?.needIds.length ?? 0) + (applied?.serviceIds.length ?? 0);
    const matchedContactCount = applied?.matchedContactCount ?? 0;

    // Nothing pre-ticked is the ordinary case: most onboarded people are not ingested contacts.
    const nothingPreTicked = Boolean(applied) && suggestedCount === 0;

    return (
        <li className="rounded-lg border border-border-norm p-4">
            <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold text-text-900">{accountName}</h3>
                <Button className="ml-auto" size="small" category="weak" onClick={onRemove}>
                    {c('Onboarding action').t`Remove this account`}
                </Button>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-4">
                <div>
                    <p className="mb-2 text-sm font-medium text-text-900">{c('Onboarding field').t`Role`}</p>
                    <Select
                        options={roleOptions}
                        value={roleOptions.find((option) => option.value === attachment.roleId)}
                        onChange={(option) => onChange({ roleId: option.value })}
                        placeholder={c('Onboarding field').t`Pick a role`}
                        className="max-w-xs"
                        error={chosenRefusal?.tag}
                    />
                    {chosenRefusal && (
                        <p className="mt-2 text-sm text-danger-600" role="alert">
                            {c('Onboarding role refusal')
                                .t`This role stays picked but you can no longer grant it — ${chosenRefusal.tag}. The invitation would be refused when sent: pick another one.`}
                        </p>
                    )}
                    <p className="mt-2 text-xs text-text-600">
                        {c('Onboarding hint').t`The role carries what the person may do on this account.`}
                    </p>
                </div>

                <div className="lg:col-span-3">
                    <p className="mb-2 text-sm font-medium text-text-900">{c('Onboarding field').t`Perimeter`}</p>
                    <RadioGroup
                        name={`scope-${attachment.organizationId}`}
                        value={attachment.dataScope}
                        onValue={(value: DataScope) =>
                            // Leaving the scope drops the market selection and its snapshot:
                            // keeping them would show a perimeter nobody set.
                            onChange({ dataScope: value, needIds: [], serviceIds: [], suggestion: null })
                        }
                        category="card"
                        options={[
                            {
                                value: DataScope.All,
                                label: c('Onboarding perimeter').t`The whole activity of the accounts`,
                                description: c('Onboarding perimeter')
                                    .t`An evolving perimeter: a need or a service attached to these accounts later becomes visible on its own.`,
                            },
                            {
                                value: DataScope.Restricted,
                                label: c('Onboarding perimeter').t`Pick some activities`,
                                // The mockup promises an auto-updating perimeter here; the API does
                                // not: the id list is frozen at invitation and the source never revises it.
                                description: c('Onboarding perimeter')
                                    .t`A perimeter frozen on what is ticked below. An activity attached to this person later stays invisible until someone comes back and adds it.`,
                            },
                        ]}
                    />
                </div>
            </div>

            {restricted && (
                <div className="mt-4 border-t border-border-norm pt-4">
                    {/* The search field lives here, not in the picker: a control that filters
                        must not mount under what it filters, or each keystroke unmounts it. */}
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        <div className="grow">
                            <InputField
                                id={`activity-search-${attachment.organizationId}`}
                                label={c('Onboarding activity').t`Search an activity`}
                                type="search"
                                value={search}
                                onValue={setSearch}
                            />
                        </div>
                        <span className="shrink-0 rounded-full border border-border-norm px-3 py-1 text-xs text-text-600">
                            {selectedCount === 0
                                ? c('Onboarding activity').t`No activity selected`
                                : c('Onboarding activity').t`${selectedCount} selected`}
                        </span>
                    </div>

                    {activitiesLoading && !activities && <ListSkeleton rows={6} withControl />}

                    {activitiesError && (
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                            <p className="text-xs text-danger-700" role="alert">
                                {c('Onboarding')
                                    .t`The activities of this account could not be read, so nothing is listed and nothing is pre-ticked.`}
                            </p>
                            <Button category="weak" size="small" onClick={() => refetchActivities()}>
                                {c('Onboarding action').t`Try again`}
                            </Button>
                        </div>
                    )}

                    {nothingPreTicked && (
                        <div className="mb-3">
                            <Button category="weak" size="small" onClick={onEditIdentity}>
                                {c('Onboarding action').t`Check the address`}
                            </Button>
                        </div>
                    )}

                    {matchedContactCount > 1 && (
                        <p className="mb-3 text-xs text-danger-700" role="alert">
                            {c('Onboarding suggestion')
                                .t`This address is carried by several ingested contacts, so what is pre-ticked mixes their activities. Check each line before sending.`}
                        </p>
                    )}

                    {activities && (
                        <ActivityPicker
                            activities={activities}
                            needIds={attachment.needIds}
                            serviceIds={attachment.serviceIds}
                            onToggleNeed={toggleNeed}
                            onToggleService={toggleService}
                            onToggleMarket={toggleMarket}
                        />
                    )}
                </div>
            )}
        </li>
    );
};

export default AttachmentRow;
